import MediaFrame from "@/components/feature/MediaFrame";
import Reveal from "@/components/feature/Reveal";
import Breadcrumb from "@/components/feature/Breadcrumb";
import type { SkinTreatment, SkinTreatmentCategory } from "@/mocks/skinTreatments";

/** 施術方式（4分類）の表示名。お肌管理 親ページの分類表記に合わせる。 */
const CATEGORY_LABEL: Record<SkinTreatmentCategory, string> = {
  dermapen: "DERMAPEN 4",
  peeling: "PEELING",
  plasma: "PLASMA",
  "photo-facial": "PHOTO FACIAL",
};

/**
 * お肌管理 個別施術ページのFV。
 * 画像は文字なしのビジュアル専用のため、番号・カテゴリ名・日本語施術名・英字施術名は HTML で表示する。
 * PC では「左：テキスト 約45% / 右：画像 約55%」の2カラム、スマートフォンでは縦積みにする。
 * 画像枠は全10ページで aspect-[3/2] に統一し、object-cover で表示する。
 */
export default function SkinTreatmentHero({ data }: { data: SkinTreatment }) {
  return (
    <section className="relative overflow-hidden bg-background-50 pb-10 pt-24 md:pb-12 md:pt-28 lg:pb-14 lg:pt-32">
      <div
        aria-hidden
        className="pointer-events-none absolute -right-24 top-24 h-72 w-72 rounded-full bg-secondary-100/50 blur-3xl"
      />

      <div className="u-container relative">
        {/* 施術詳細：HOME ＞ 親カテゴリ ＞ 現在の施術（現在ページは非リンク） */}
        <Breadcrumb
          items={[
            { label: "HOME", href: "/", path: "/" },
            { label: "お肌管理", href: "/treatment/skin-management", path: "/treatment/skin-management" },
            { label: data.jp, path: `/skin/${data.slug}` },
          ]}
        />

        <div className="mx-auto mt-8 grid w-full max-w-[1180px] grid-cols-1 gap-8 md:mt-10 lg:grid-cols-[0.45fr_0.55fr] lg:items-center lg:gap-14">
          <div>
            <Reveal>
              <div className="flex items-center gap-4">
                <span className="font-label text-[13px] tracking-[0.3em] text-secondary-600">{data.no}</span>
                <span className="h-px w-8 bg-accent-500" />
                <span className="en-eyebrow text-[11px] text-secondary-700 md:text-[12px]">
                  {CATEGORY_LABEL[data.category]}
                </span>
              </div>
              <h1 className="mt-7 font-heading text-[30px] leading-[1.5] tracking-[0.02em] text-foreground-950 md:text-[40px]">
                {data.jp}
              </h1>
              <p className="mt-4 font-label text-[12px] tracking-[0.28em] text-foreground-500 md:text-[13px]">
                {data.en}
              </p>
            </Reveal>
          </div>

          <Reveal delay={120}>
            <MediaFrame
              src={data.heroImage || undefined}
              alt={data.heroImageAlt}
              tone="greige"
              icon="ri-image-line"
              label="TREATMENT VISUAL"
              note={data.heroNote}
              objectPosition={data.heroObjectPosition ?? "object-center"}
              className="aspect-[3/2] w-full"
            />
          </Reveal>
        </div>
      </div>
    </section>
  );
}